<script setup lang="ts">
import { onFlash } from '~/composables/flash';
import type { Flash, MessageType, FlashMessage } from '~/composables/flash';

const state = reactive({ flashes: [] as Flash[], errors: [] as Flash[] });
onFlash('exception', (message: FlashMessage) => {
  state.errors.push({ ...message, type: 'exception' });
});
onFlash('error', (message: FlashMessage) => {
  state.errors.push({ ...message, type: 'error' });
});
onFlash('warning', (message: FlashMessage) => {
  state.flashes.push({ ...message, type: 'warning' });
});
onFlash('info', (message: FlashMessage) => {
  state.flashes.push({ ...message, type: 'info' });
});
onFlash('success', (message: FlashMessage) => {
  state.flashes.push({ ...message, type: 'success' });
});
onFlash('clear', () => {
  state.flashes.splice(0);
  state.errors.splice(0);
});
function removeFlash(index: number) {
  state.flashes.splice(index, 1);
}
function iconForType(type: MessageType): string {
  switch (type) {
    case 'exception':
    case 'error':
      return 'ic:round-error-outline';
    case 'warning':
      return 'heroicons:bell-alert';
    case 'info':
      return 'heroicons:information-circle';
    case 'success':
      return 'heroicons:check-circle';
  }
}
const errorModalOpen = computed({
  get: () => state.errors.length > 0,
  set: () => (state.errors = []),
});
</script>
<template>
  <div>
    <div v-if="state.flashes.length > 0" class="flex flex-col mb-4">
      <div v-for="(flash, i) in state.flashes" :key="i">
        <UAlert
          :icon="iconForType(flash.type)"
          type="error"
          class="mb-2"
          :color="flash.type === 'exception' ? 'error' : flash.type"
          variant="subtle"
          close
          @update:open="removeFlash(i)"
        >
          <template #title
            ><span class="font-bold">{{ flash.title }}</span></template
          >
          <template #description>
            <span v-html="flash.description" />
          </template>
        </UAlert>
      </div>
    </div>
    <UModal v-model:open="errorModalOpen" :ui="{ overlay: 'z-[100]', content: 'z-[100]' }">
      <template #title>
        <UIcon :name="iconForType('error')" class="text-error align-text-top" />
        {{ $t('general.error.title') }}
      </template>

      <template #body>
        <div v-for="(flash, i) in state.errors" :key="i">
          <UAlert class="mb-2" :color="flash.type === 'exception' ? 'error' : flash.type" variant="subtle">
            <template #title>
              <span class="font-bold">{{ flash.title }}</span>
            </template>
            <template #description>
              <div v-if="flash.description" class="text-default mb-2" v-html="flash.description" />
              <UPageList v-if="flash.details">
                <div v-for="(detail, j) in flash.details" :key="j">- {{ detail }}</div>
              </UPageList>
            </template>
          </UAlert>
        </div>
      </template>

      <template #footer>
        <div class="flex justify-end w-full">
          <UButton :label="$t('button.close')" @click="state.errors = []" />
        </div>
      </template>
    </UModal>
  </div>
</template>
